import {DataStatus} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {testIds, workshopRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  queryParams,
  Section,
  workshopSearchParams,
} from '@dms/shared';

import {composePath} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

interface ActiveServiceCasesProps {
  vehicleId: string;
}

const ACTIVE_SERVICE_CASES_GRID_CODE = 'service-cases-active';
const ACTIVE_SERVICE_ORDERS_GRID_CODE = 'service-orders-active';
const SERVICE_CASE_PREDEFINED_COLUMNS = ['state'] as const;
const SERVICE_ORDER_PREDEFINED_COLUMNS = ['serviceCaseId'] as const;

export function ActiveServiceCases(props: ActiveServiceCasesProps) {
  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {vehicleId: props.vehicleId ?? null}]);

  const actionServiceCaseCallback: ActionCallback<typeof SERVICE_CASE_PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    const serviceCaseState = getColumnData<{key: string}>('state')?.value?.key;
    const serviceCaseId = isArray(rowId) ? rowId[0] : rowId;

    const path =
      serviceCaseState === 'DRAFT'
        ? composePath(workshopRoutes.draftServiceCase, {
            params: {id: serviceCaseId},
            queryParams: {
              [queryParams.COMPONENT_SECTIONS_TAB]: workshopSearchParams.create.serviceJobs,
            },
          })
        : composePath(workshopRoutes.serviceCaseDetail, {
            params: {id: serviceCaseId},
          });

    match(actionKey)
      .with('redirectDetail', 'redirectDetail_newTab', () => window.open(path))
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const actionServiceOrderCallback: ActionCallback<typeof SERVICE_ORDER_PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    const serviceCaseId = getColumnData<string>('serviceCaseId')?.value;
    const serviceOrderId = isArray(rowId) ? rowId[0] : rowId;

    const path = composePath(workshopRoutes.serviceCaseDetail, {
      params: {
        id: serviceCaseId ?? '',
      },
      queryParams: {
        [queryParams.COMPONENT_SECTIONS_SECTION]: workshopSearchParams.serviceDetail.orders,
        [queryParams.SERVICE_CASE_ORDER_ID]: serviceOrderId,
      },
    });

    match(actionKey)
      .with('redirectDetail', 'redirectDetail_newTab', () => window.open(path))
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <Section
      isFullHeight
      data-testid={testIds.workshop.serviceCaseDetail('activeServiceCases')}
      tabs={[
        {
          content: (
            <DataStatus
              isLoading={isNil(props.vehicleId)}
              key="activeServiceCases-serviceCases"
              minHeight={175}
            >
              <DmsDataGrid
                gridCode={ACTIVE_SERVICE_CASES_GRID_CODE}
                actionCallback={actionServiceCaseCallback}
                predefinedColumns={SERVICE_CASE_PREDEFINED_COLUMNS}
                queryModifier={queryModifier}
                data-testid={testIds.workshop.serviceCaseDetail('activeServiceCases-serviceCases')}
              />
            </DataStatus>
          ),
          queryId: 'serviceCases',
          title: i18n.t('page.workshop.labels.serviceCases'),
        },
        {
          content: (
            <DataStatus
              isLoading={isNil(props.vehicleId)}
              key="activeServiceCases-serviceOrders"
              minHeight={175}
            >
              <DmsDataGrid
                gridCode={ACTIVE_SERVICE_ORDERS_GRID_CODE}
                actionCallback={actionServiceOrderCallback}
                predefinedColumns={SERVICE_ORDER_PREDEFINED_COLUMNS}
                queryModifier={queryModifier}
                data-testid={testIds.workshop.serviceCaseDetail('activeServiceCases-serviceOrders')}
              />
            </DataStatus>
          ),
          queryId: 'serviceOrders',
          title: i18n.t('page.workshop.labels.serviceOrders'),
        },
      ]}
    />
  );
}
